import type { Icon } from "@phosphor-icons/react";
import { CoinsIcon } from "@phosphor-icons/react/dist/csr/Coins";
import { CurrencyDollarSimpleIcon } from "@phosphor-icons/react/dist/csr/CurrencyDollarSimple";
import { CurrencyEurIcon } from "@phosphor-icons/react/dist/csr/CurrencyEur";
import { CurrencyGbpIcon } from "@phosphor-icons/react/dist/csr/CurrencyGbp";
import { CurrencyInrIcon } from "@phosphor-icons/react/dist/csr/CurrencyInr";
import { CurrencyJpyIcon } from "@phosphor-icons/react/dist/csr/CurrencyJpy";
import { createContext, type ReactNode, useContext } from "react";

/** The currency a report's money is in when its script does not say. */
export const DEFAULT_CURRENCY = "USD";

/**
 * The report's ISO 4217 code, as the API sent it (`output.currency`,
 * `overview.currency`), or the default when it sent none or one the browser
 * cannot format: a bad code must not take the page down with a RangeError.
 */
export const currencyOf = (code: string | null | undefined) => {
  if (!code) return DEFAULT_CURRENCY;
  try {
    new Intl.NumberFormat(undefined, { style: "currency", currency: code });
    return code.toUpperCase();
  } catch {
    return DEFAULT_CURRENCY;
  }
};

/** Money in the report's currency, whole units unless asked otherwise. */
export const formatMoney = (
  value: number,
  currency: string = DEFAULT_CURRENCY,
  options: Intl.NumberFormatOptions = { maximumFractionDigits: 0 },
) =>
  withTrueMinus(value, {
    ...options,
    style: "currency",
    currency: currencyOf(currency),
  });

/**
 * A number in the reader's locale, its minus sign a true minus. Read from
 * Intl's parts, not the string, so a locale's marks around the sign (RTL
 * direction marks) do not hide it.
 */
export const withTrueMinus = (
  value: number,
  options: Intl.NumberFormatOptions,
) =>
  new Intl.NumberFormat(undefined, options)
    .formatToParts(value)
    .map((part) => (part.type === "minusSign" ? "\u2212" : part.value))
    .join("");

/** A signed amount in whole units, with a true minus: "+$761", "−€980". */
export const moneyChange = (
  change: number,
  currency: string = DEFAULT_CURRENCY,
) => `${change < 0 ? "\u2212" : "+"}${formatMoney(Math.abs(change), currency)}`;

/** "$", "€", "£": the currency's own sign, the way the page writes it. */
export const currencySymbol = (currency: string = DEFAULT_CURRENCY) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyOf(currency),
    currencyDisplay: "narrowSymbol",
  })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value ?? currency;

const SYMBOL_ICON: Record<string, Icon> = {
  $: CurrencyDollarSimpleIcon,
  "€": CurrencyEurIcon,
  "£": CurrencyGbpIcon,
  "¥": CurrencyJpyIcon,
  "₹": CurrencyInrIcon,
};

/** A money column's icon: its currency's sign, or coins for one without a glyph. */
export const currencyIcon = (currency: string = DEFAULT_CURRENCY): Icon =>
  SYMBOL_ICON[currencySymbol(currency)] ?? CoinsIcon;

const Context = createContext<string>(DEFAULT_CURRENCY);

/** The currency the report on screen declared, for everything that shows money. */
export const ReportCurrencyProvider = ({
  currency,
  children,
}: {
  currency: string | null | undefined;
  children: ReactNode;
}) => (
  <Context.Provider value={currencyOf(currency)}>{children}</Context.Provider>
);

export const useReportCurrency = () => useContext(Context);
